// 공정 계획 대비 판정·실적 날짜 — 저장소 밖 한 벌(인메모리·Supabase·화면이 같이 쓴다). 판정 기준은 지금 계획(start/end, 착공일 변경·변경공사 반영).

import type { ProjectPhase, ProjectPhaseStatus, ProjectStatus } from "./types";

export const MAX_SCHEDULE_REASON = 200;

export const PHASE_SCHEDULE_KINDS = ["NO_PLAN", "NORMAL", "START_LATE", "LATE", "EARLY", "NO_RECORD"] as const;
export type PhaseScheduleKind = (typeof PHASE_SCHEDULE_KINDS)[number];

export interface PhaseSchedule {
  kind: PhaseScheduleKind;
  // 어긋난 달력 일수(정상·계획일 없음·완료일 없음은 0).
  days: number;
}

export const PHASE_SCHEDULE_LABEL: Record<PhaseScheduleKind, string> = {
  NO_PLAN: "계획일 없음",
  NORMAL: "정상",
  START_LATE: "착수 지연",
  LATE: "지연",
  EARLY: "조기 완료",
  NO_RECORD: "완료일 없음",
};

// 사유를 받을 판정 — 완료 처리 때 입력칸을 열고, 나중에 고칠 수 있다.
export const REASON_KINDS: readonly PhaseScheduleKind[] = ["START_LATE", "LATE", "EARLY"];

const DAY_MS = 86_400_000;

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);

export type SchedulePhase = Pick<
  ProjectPhase,
  "status" | "start_date" | "end_date" | "actual_start_date" | "actual_end_date"
>;

// 지연은 지금 계획(start/end)으로만 판정한다 — baseline_* 는 최초 계획 이력(표시용)이라 판정에 쓰지 않는다(착공일 변경·변경공사로 옮긴 일정이 지연으로 잡히면 안 된다).
export const phaseSchedule = (phase: SchedulePhase, todayKst: string): PhaseSchedule => {
  const { start_date: start, end_date: end } = phase;
  if (start === null || end === null) return { kind: "NO_PLAN", days: 0 };

  if (phase.status === "DONE") {
    const done = phase.actual_end_date;
    if (done === null) return { kind: "NO_RECORD", days: 0 };
    if (done > end) return { kind: "LATE", days: daysBetween(end, done) };
    if (done < end) return { kind: "EARLY", days: daysBetween(done, end) };
    return { kind: "NORMAL", days: 0 };
  }

  // 보류는 착수 기록이 있어야 「시작한 공정」 이다(대기에서 바로 보류한 공정은 아직 안 시작) — 안 시작한 공정은 끝날이 지나도 착수 지연.
  const notStarted = phase.status === "PENDING" || (phase.status === "HOLD" && phase.actual_start_date === null);
  if (notStarted) {
    return todayKst > start ? { kind: "START_LATE", days: daysBetween(start, todayKst) } : { kind: "NORMAL", days: 0 };
  }
  if (todayKst > end) return { kind: "LATE", days: daysBetween(end, todayKst) };
  const began = phase.actual_start_date;
  if (began !== null && began > start) return { kind: "START_LATE", days: daysBetween(start, began) };
  return { kind: "NORMAL", days: 0 };
};

// 화면 톤에 쓸 판정 — 착수 지연이라도 계획 끝날까지 지났으면 지연과 같은 톤(상태값은 START_LATE 그대로).
export const scheduleToneKind = (
  phase: Pick<SchedulePhase, "end_date">,
  schedule: PhaseSchedule,
  todayKst: string
): PhaseScheduleKind =>
  schedule.kind === "START_LATE" && phase.end_date !== null && todayKst > phase.end_date ? "LATE" : schedule.kind;

// 지금 늦어지는 판정 — 끝난 공정의 지난 지연(DONE 의 LATE)은 세지 않는다.
export const DELAY_KINDS: readonly PhaseScheduleKind[] = ["START_LATE", "LATE"];

// 지연을 셀 현장 — 공정을 고칠 수 있는 시공중만(준공 뒤 남은 대기 공정은 지연이 아니다).
export const countsPhaseDelay = (status: ProjectStatus): boolean => status === "IN_PROGRESS";

// 공정 지연 건수 — 판정은 phaseSchedule 한 벌을 그대로 쓴다.
export const delayedPhaseCount = (phases: readonly SchedulePhase[], todayKst: string): number =>
  phases.filter((p) => p.status !== "DONE" && DELAY_KINDS.includes(phaseSchedule(p, todayKst).kind)).length;

// 최초 계획으로 굳힐 날짜 — 시작·끝이 둘 다 있을 때만(`0066` 트리거와 같다).
export const baselineOf = (
  start: string | null,
  end: string | null
): Pick<ProjectPhase, "baseline_start_date" | "baseline_end_date"> =>
  start !== null && end !== null
    ? { baseline_start_date: start, baseline_end_date: end }
    : { baseline_start_date: null, baseline_end_date: null };

// 「지연 3일」 · 「정상」.
export const phaseScheduleText = (s: PhaseSchedule): string =>
  s.days > 0 ? `${PHASE_SCHEDULE_LABEL[s.kind]} ${s.days}일` : PHASE_SCHEDULE_LABEL[s.kind];

export interface PhaseActuals {
  actual_start_date: string | null;
  actual_end_date: string | null;
  // 빈 사유면 보내지 않는다 — RPC 가 지금 사유를 그대로 둔다.
  schedule_reason?: string;
}

// 상태를 바꿀 때 실적 날짜 — 진행 첫날을 남기고 완료는 오늘 · 대기로 되돌리면 지우고 완료를 되돌리면 완료일만 지운다.
export const phaseActualsOnTransition = (
  phase: Pick<ProjectPhase, "actual_start_date">,
  to: ProjectPhaseStatus,
  todayKst: string,
  reason?: string
): PhaseActuals => {
  const given = reason?.trim() ?? "";
  const withReason = (a: PhaseActuals): PhaseActuals =>
    to === "DONE" && given !== "" ? { ...a, schedule_reason: given } : a;
  switch (to) {
    case "ACTIVE":
      return { actual_start_date: phase.actual_start_date ?? todayKst, actual_end_date: null };
    case "DONE":
      return withReason({ actual_start_date: phase.actual_start_date, actual_end_date: todayKst });
    case "HOLD":
      return { actual_start_date: phase.actual_start_date, actual_end_date: null };
    case "PENDING":
      return { actual_start_date: null, actual_end_date: null };
  }
};

// 오늘 완료하면 사유를 물을지 — 계획 끝날과 다른 날 끝낼 때만.
export const completionAsksReason = (phase: SchedulePhase, todayKst: string): boolean =>
  REASON_KINDS.includes(
    phaseSchedule({ ...phase, status: "DONE", actual_end_date: todayKst }, todayKst).kind
  );
